// super_admin-only: hotel logo + Wi-Fi details printed on every in-room QR card.
import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Save, Trash2, Wifi } from 'lucide-react';
import { API_URL, apiCall, getToken, readCookie } from './_shared';

const QRCardDesign = () => {
  const [form, setForm] = useState({ wifi_ssid: '', wifi_password: '', footer_text: '' });
  const [logoUrl, setLogoUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const b = await apiCall('/api/admin/inroom/qr-branding');
      setForm({ wifi_ssid: b.wifi_ssid || '', wifi_password: b.wifi_password || '', footer_text: b.footer_text || '' });
      setLogoUrl(b.logo_url || null);
    } catch (e) { setMsg(e.message); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await apiCall('/api/admin/inroom/qr-branding', { method: 'PUT', body: JSON.stringify(form) });
      setMsg('Saved — new QR sheets will use these details.');
    } catch (e) { setMsg(e.message); }
    finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const csrf = readCookie('csrf_token');
      const r = await fetch(`${API_URL}/api/admin/inroom/qr-branding/logo`, {
        method: 'POST', body: fd, credentials: 'include',
        headers: { Authorization: `Bearer ${getToken()}`, ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Upload failed');
      setLogoUrl(data.logo_url);
      setMsg('Logo uploaded.');
    } catch (e) { setMsg(e.message); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const removeLogo = async () => {
    try {
      const b = await apiCall('/api/admin/inroom/qr-branding/logo', { method: 'DELETE' });
      setLogoUrl(b.logo_url || null);
    } catch (e) { setMsg(e.message); }
  };

  const field = (k, label, ph) => (
    <label className="block text-xs text-slate-600">
      {label}
      <input
        value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        placeholder={ph}
        className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
        data-testid={`qr-design-${k.replace('_', '-')}`}
      />
    </label>
  );

  return (
    <div className="bg-white rounded-xl border p-4" data-testid="qr-card-design">
      <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
        <Wifi className="w-4 h-4 text-orange-500" /> QR card design
        <span className="text-[10px] font-normal text-slate-400">super_admin</span>
      </h3>
      <p className="text-xs text-slate-500 mt-1">Logo + Wi-Fi details are printed on every room's QR card.</p>

      <div className="mt-3 grid md:grid-cols-[140px_1fr] gap-4">
        <div>
          <div className="h-20 w-full border-2 border-dashed rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden" data-testid="qr-design-logo-preview">
            {logoUrl ? <img src={`${API_URL}${logoUrl}`} alt="Hotel logo" className="max-h-16 max-w-full object-contain" /> : <span className="text-[11px] text-slate-400">No logo</span>}
          </div>
          <div className="flex gap-1 mt-2">
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex-1 text-xs px-2 py-1.5 rounded bg-slate-800 text-white hover:bg-slate-700 flex items-center justify-center gap-1 disabled:opacity-60" data-testid="qr-design-logo-upload">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />} Logo
            </button>
            {logoUrl && (
              <button onClick={removeLogo} className="text-xs px-2 py-1.5 rounded border hover:bg-red-50 text-red-600" title="Remove logo" data-testid="qr-design-logo-remove">
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="qr-design-logo-input" />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {field('wifi_ssid', 'Wi-Fi network', 'LaRosaNegra-Guest')}
          {field('wifi_password', 'Wi-Fi password', 'beach2026')}
          {field('footer_text', 'Footer line (optional)', 'Reception: +382 69 533 984')}
        </div>
      </div>

      <div className="flex items-center gap-3 mt-3">
        <button onClick={save} disabled={saving} className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-60" data-testid="qr-design-save">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save design
        </button>
        {msg && <span className="text-xs text-slate-600" data-testid="qr-design-msg">{msg}</span>}
      </div>
    </div>
  );
};

export default QRCardDesign;
